import { getSolicitacaoByID } from "@/http/getSolicitacaoByID";
import { novaResposta } from "@/http/novaResposta";
import { queryClient } from "@/lib/react-query";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UserNav } from "../userNav";
import { ItemResponse } from "./components/ItemResponse";
import { ItemArquivosSolicitacao } from "./components/ItemArquivosSolicitacao";
import { ArrowLeft, ListCheck, LoaderCircle, Logs } from "lucide-react";
import { ItemResponseAtendente } from "./components/ItemResponseAtendente";
import { ItemArquivos } from "./components/ItemArquivos";
import moment from "moment";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import ReactQuill from "react-quill-new";
import getServico from "@/utils/getServico";
import getLabel from "@/utils/getLabel";
import getUnidade from "@/utils/getUnidade";
import { encurtarNome } from "@/utils/encurtarNome";
import "react-quill-new/dist/quill.snow.css";
import { finalizarSolicitacao } from "@/http/finalizarSolicitacao";
import { checkSolicitacao } from "@/http/checkSolicitacao";
import { ModalLogs } from "../modalLogs";
import Empty from "../empty";
import { getTempoResposta } from "@/utils/getTempoResposta";
import { useNavigate } from "react-router-dom";
import { BadgeStatus } from "../badgeStatus";
import { getUserId } from "@/utils/getUserId";
import { assumirSolicitacao } from "@/http/assumirSolicitacao";
import { NavLinks } from "../navLinks";
import { useAuth } from "@/contexts/auth";
import { VIEWS } from "@/constants/views";

export function ViewDetalhe({ user, id }) {
  const [resposta, setResposta] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputArquivoRef = useRef(null);
  const [loadingFinalizar, setLoadingFinalizar] = useState(false);
  const [loadingAssumir, setLoadingAssumir] = useState(false);
  const { setView, setDetalhe } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["getSolicitacaoByID", id],
    queryFn: () => getSolicitacaoByID(id),
    retry: true,
  });

  async function handleResponder() {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", id);

      if (resposta.replace("<p><br></p>", "")) {
        formData.append("resposta", resposta);
      }

      if (arquivo) {
        formData.append("arquivo", arquivo);
      }

      await novaResposta(formData);

      setResposta("");
      setArquivo(null);
      inputArquivoRef.current.value = "";
      queryClient.invalidateQueries(["getSolicitacaoByID", id]);

      toast.success("Resposta enviada com sucesso!");
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleFinalizar() {
    try {
      setLoadingFinalizar(true);
      await finalizarSolicitacao(id);
      queryClient.invalidateQueries(["getSolicitacaoByID", id]);
      toast.success("Solicitação finalizada com sucesso!");
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoadingFinalizar(false);
    }
  }

  async function handleAssumir() {
    try {
      setLoadingAssumir(true);
      await assumirSolicitacao(id);
      queryClient.invalidateQueries(["getSolicitacaoByID", id]);
      toast.success("Solicitação assumida com sucesso!");
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoadingAssumir(false);
    }
  }

  function handleVoltar() {
    setView(VIEWS.VIEW_DASH);
    navigate(-1);
    setDetalhe(null);
  }

  useEffect(() => {
    checkSolicitacao(id);
    window.history.replaceState({}, "", "/admin");
  }, [id]);

  const userID = getUserId(user.token);
  const atendente = data?.atendente;
  const podeResponder = atendente === userID;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <img src="/logo.png" alt="logo" className="h-[35px] animate-pulse" />
      </div>
    );
  }

  return (
    <div>
      <header className="h-[70px] border-b border-border bg-zinc-50 flex flex-row justify-between items-center px-6">
        <NavLinks />

        <div className="flex flex-row items-center gap-5">
          <UserNav />
        </div>
      </header>

      <div className="motion-opacity-in-[0%] motion-duration-[0.80s]/opacity pt-8 mx-auto max-w-[1100px]">
        <div className="flex items-end justify-between gap-6">
          <div className="flex flex-row items-center gap-4">
            <Button onClick={handleVoltar} className="cursor-pointer">
              <ArrowLeft />
              Voltar
            </Button>
            <div className="flex flex-col">
              <p className="text-2xl font-bold tracking-tight">
                Detalhe da solicitação
              </p>
              <p className="text-[#b2b2b2] text-[12px]">{id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-muted-foreground text-sm">
              Enviado:{" "}
              {data
                ? `${moment(data.createdAt).format("DD/MM/YYYY HH:mm")}`
                : "--/--/-- --:--"}
            </p>
            {data && (
              <BadgeStatus
                finalizado={data.finalizado}
                inProgress={data.inProgress}
              />
            )}
          </div>
        </div>
      </div>

      <div className="motion-scale-in-[0.95] motion-translate-x-in-[0%] motion-translate-y-in-[6%] motion-opacity-in-[10%] motion-duration-[0.27s]/scale motion-duration-[0.27s]/opacity relative my-6 p-4 mx-auto max-w-[1100px] bg-white border border-border rounded-md">
        {!atendente && !data?.finalizado && (
          <Button
            disabled={loadingAssumir}
            onClick={handleAssumir}
            size="sm"
            className="absolute top-4 right-4 cursor-pointer text-[#fff]"
          >
            {loadingAssumir && (
              <LoaderCircle className="animate-spin text-slate-50 size-5" />
            )}
            Assumir Solicitação
          </Button>
        )}
        <div className="border-gray-100">
          <dl className="divide-y divide-gray-100">
            {data && (
              <ItemResponse
                label="Serviço"
                value={getServico(data.setor, data.servico)}
              />
            )}
            {data && !data.finalizado && (
              <ItemResponse
                label="Esperando"
                value={moment(data.createdAt).fromNow()}
              />
            )}
            {data && data.finalizado && (
              <ItemResponse
                label="Tempo de resposta"
                value={getTempoResposta(data.createdAt, data.finalizado_data)}
              />
            )}
            {data?.solicitacao
              .filter(
                (item) => !(item.label && item.label.startsWith("arquivo_")),
              )
              .map((item, idx) => (
                <ItemResponse
                  label={getLabel(data.setor, data.servico, item.label)}
                  value={item.resposta}
                  key={idx}
                />
              ))}
            {data && (
              <ItemResponse
                label="Unidade"
                value={data.unidade_origem || getUnidade(data.unidade)}
              />
            )}
            {data?.solicitacao.filter(
              (item) => item.label && item.label.startsWith("arquivo_"),
            ).length > 0 && (
              <ItemArquivosSolicitacao
                id={id}
                setor={data?.setor}
                servico={data?.servico}
                arquivos={data?.solicitacao.filter(
                  (item) => item.label && item.label.startsWith("arquivo_"),
                )}
              />
            )}
          </dl>
        </div>
      </div>

      <div className="my-6 p-4 mx-auto max-w-[1100px] bg-white border border-border rounded-md flex flex-col gap-4">
        <div className="w-full flex justify-between">
          <p className="text-2xl font-bold tracking-tight">
            Respostas enviadas
          </p>
          <ModalLogs id={id}>
            <Button
              variant="secondary"
              size="sm"
              className="w-[120px] bg-zinc-600 cursor-pointer text-white hover:text-zinc-600"
            >
              <Logs />
              Logs
            </Button>
          </ModalLogs>
        </div>

        {data?.respostas.length > 0 && (
          <div className="mt-4">
            <div className="border-gray-100">
              <dl className="divide-y divide-gray-100">
                {data.respostas
                  .filter((item) => item.type === "text")
                  .map((item, idx) => (
                    <ItemResponseAtendente
                      label={encurtarNome(item.usuario_nome)}
                      value={item.resposta}
                      key={idx}
                      hora={moment(item.createdAt).format("DD/MM/YYYY HH:mm")}
                    />
                  ))}
                {data.respostas.filter(
                  (item) =>
                    item.type === "arquivo" || item.type === "arquivo_link",
                ).length > 0 && (
                  <ItemArquivos
                    id={id}
                    arquivos={data.respostas.filter(
                      (item) =>
                        item.type === "arquivo" || item.type === "arquivo_link",
                    )}
                  />
                )}
              </dl>
            </div>
          </div>
        )}

        {data?.respostas.length === 0 && (
          <div className="flex flex-col gap-2 items-center p-8">
            <Empty />
            <p className="text-[#b2b2b2] text-[12px]">
              Nenhum registro encontrado.
            </p>
          </div>
        )}
      </div>

      {data?.respostas.length > 0 && !data.finalizado && podeResponder && (
        <div className="my-6 p-4 mx-auto max-w-[1100px] bg-white border border-border rounded-md flex flex-col gap-4">
          <div className="w-full flex justify-between">
            <p className="text-2xl font-bold tracking-tight">
              {data.finalizado
                ? "Atendimento finalizado"
                : "Finalizar atendimento"}
            </p>
            <Button
              disabled={loadingFinalizar}
              onClick={handleFinalizar}
              variant="secondary"
              className="w-[120px] bg-emerald-600 cursor-pointer text-white hover:text-emerald-600"
            >
              {loadingFinalizar ? (
                <LoaderCircle className="animate-spin text-slate-50 size-5" />
              ) : (
                <ListCheck />
              )}
              Finalizar
            </Button>
          </div>
        </div>
      )}

      {data?.finalizado && (
        <div className="my-6 p-4 mx-auto max-w-[1100px] bg-white border border-border rounded-md flex flex-col gap-4">
          <div className="w-full flex justify-between">
            <p className="text-2xl font-bold tracking-tight">
              {data.finalizado
                ? "Atendimento finalizado"
                : "Finalizar atendimento"}
            </p>
            <div className="flex flex-row gap-1">
              <p className="text-muted-foreground text-sm">
                Finalizado {moment(data.finalizado_data).format("DD/MM/YYYY")}{" "}
                às {moment(data.finalizado_data).format("HH:mm")} por{" "}
                <strong>{encurtarNome(data.finalizado_usuario)}</strong>
              </p>
            </div>
          </div>
        </div>
      )}

      {data && podeResponder && !data?.finalizado && (
        <div className="my-6 p-4 mx-auto max-w-[1100px] bg-white border border-border rounded-md flex flex-col gap-4">
          <p className="text-2xl font-bold tracking-tight">Responder</p>
          <ReactQuill value={resposta} onChange={setResposta} />
          <Input
            ref={inputArquivoRef}
            type="file"
            onChange={(event) => setArquivo(event.currentTarget.files[0])}
          />
          <div className="w-full flex justify-end">
            <div className="w-[200px]">
              <Button
                onClick={handleResponder}
                className="w-full cursor-pointer"
                disabled={
                  loading || (!arquivo && !resposta.replace("<p><br></p>", ""))
                }
              >
                {loading && (
                  <LoaderCircle className="animate-spin text-slate-50 size-5" />
                )}
                Enviar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
